// src/components/MediaDetailsModal.tsx
// Shared media details modal — used by AlbumView (library) and DemoDashboard
// (recent uploads) so a tap/click opens the same detail view everywhere,
// matching the mobile apps. Manages its own voiceover-status state.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiService, VoiceoverStatusResponse } from "../services/api";
import { getUploadPhase } from "../utils/uploadPhase";
import VerifyOriginalModal from "./VerifyOriginalModal";
import SupportTicketModal from "./SupportTicketModal";
import { VALIDATION_FAILED_TITLE, VALIDATION_FAILED_REASONS, VALIDATION_FAILED_HELP } from "../constants/validationCopy";

function formatBytes(bytes?: number): string {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB", "TB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))}${sizes[i]}`;
}
function formatDuration(seconds?: number): string {
  if (!seconds) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, "0")}`;
}
function formatDateUK(dateString: any) {
  if (!dateString) return "—";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "—";
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day}/${month}/${year}, ${hours}:${minutes}`;
  } catch {
    return "—";
  }
}

const Spinner = ({ className = "h-4 w-4 mr-2 inline-block align-middle" }: { className?: string }) => (
  <svg className={`${className} animate-spin`} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.15)" strokeWidth="4" />
    <path d="M22 12a10 10 0 00-10-10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
  </svg>
);

type Props = {
  file: any | null;
  onClose: () => void;
  onCheckVoiceoverStatus?: (mediaId: string) => Promise<VoiceoverStatusResponse>;
};

export default function MediaDetailsModal({ file, onClose, onCheckVoiceoverStatus }: Props) {
  const navigate = useNavigate();
  const [detailsFile, setDetailsFile] = useState<any | null>(file);
  const [voiceoverStatusLoading, setVoiceoverStatusLoading] = useState(false);
  const [hasCheckedVoiceover, setHasCheckedVoiceover] = useState(false);
  const [isRefreshingVoiceover, setIsRefreshingVoiceover] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [verifyNoOriginal, setVerifyNoOriginal] = useState(false);
  const [supportOpen, setSupportOpen] = useState(false);
  const [supportPreset, setSupportPreset] = useState<{ slug?: string; subject?: string; message?: string } | null>(null);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (file) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [file]);

  // Sync internal copy when the opened file changes.
  useEffect(() => {
    setDetailsFile(file);
    setHasCheckedVoiceover(false);
    setVoiceoverStatusLoading(false);
    setIsRefreshingVoiceover(false);
  }, [file?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-check voiceover status for audio files (once).
  useEffect(() => {
    let mounted = true;
    const check = async () => {
      if (!detailsFile?.id) return;
      const isAudioFile = detailsFile.isAudio || detailsFile.type?.startsWith("audio/");
      if (!isAudioFile) return;
      if (hasCheckedVoiceover) return;
      if (
        detailsFile.voiceoverUrl !== undefined ||
        detailsFile.voiceoverGenerated !== undefined ||
        detailsFile.voiceoverPending !== undefined
      ) {
        return;
      }
      setVoiceoverStatusLoading(true);
      try {
        const response = onCheckVoiceoverStatus
          ? await onCheckVoiceoverStatus(detailsFile.id)
          : await apiService.checkVoiceoverStatus(detailsFile.id);
        if (mounted && response.success && response.data) {
          setHasCheckedVoiceover(true);
          setDetailsFile((prev: any) =>
            prev ? { ...prev, ...response.data } : null
          );
        }
      } catch (e) {
        // ignore
      } finally {
        if (mounted) {
          setVoiceoverStatusLoading(false);
          setIsRefreshingVoiceover(false);
        }
      }
    };
    const timer = setTimeout(check, 500);
    return () => {
      mounted = false;
      clearTimeout(timer);
    };
  }, [detailsFile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCheckVoiceoverStatus = async () => {
    if (!detailsFile?.id) return;
    setIsRefreshingVoiceover(true);
    setVoiceoverStatusLoading(true);
    try {
      const response = onCheckVoiceoverStatus
        ? await onCheckVoiceoverStatus(detailsFile.id)
        : await apiService.checkVoiceoverStatus(detailsFile.id);
      if (response.success && response.data) {
        setHasCheckedVoiceover(true);
        setDetailsFile((prev: any) => (prev ? { ...prev, ...response.data } : null));
      } else if (response.error) {
        alert(`Voiceover status check failed: ${response.error}`);
      }
    } catch (error: any) {
      alert(`Error checking voiceover status: ${error.message || "Unknown error"}`);
    } finally {
      setVoiceoverStatusLoading(false);
      setIsRefreshingVoiceover(false);
    }
  };

  const handleShareVoiceover = async () => {
    if (!detailsFile?.voiceoverUrl) return;
    try {
      if (navigator.share) {
        await navigator.share({ title: detailsFile.name, url: detailsFile.voiceoverUrl });
      } else {
        await navigator.clipboard.writeText(detailsFile.voiceoverUrl);
        alert("URL copied to clipboard!");
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        try {
          await navigator.clipboard.writeText(detailsFile.voiceoverUrl);
          alert("URL copied to clipboard!");
        } catch {
          alert("Could not share or copy the voiceover URL.");
        }
      }
    }
  };

  const saveBlob = (blob: Blob, filename: string) => {
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    a.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  };

  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    if (!detailsFile?.url) return;
    setDownloading(true);
    try {
      const res = await fetch(detailsFile.url);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      saveBlob(blob, detailsFile.name || detailsFile.originalFilename || "vastavik-media");
    } catch {
      window.open(detailsFile.url, "_blank");
    } finally {
      setDownloading(false);
    }
  };
  const open = () => {
    if (!detailsFile?.url) return;
    window.open(detailsFile.url, "_blank");
  };

  const [assetLoading, setAssetLoading] = useState<null | "qr" | "badge">(null);

  const downloadAsset = async (kind: "qr" | "badge") => {
    const mediaId = String(
      detailsFile?.mediaId ?? detailsFile?.meta?.mediaId ?? detailsFile?.id ?? ""
    );
    const ownerKey = detailsFile?.ownerKey ?? detailsFile?.meta?.ownerKey ?? "vastavik";
    if (!mediaId) return;
    try {
      setAssetLoading(kind);
      const blob =
        kind === "qr"
          ? await apiService.getMediaQrBlob(mediaId)
          : await apiService.getMediaBadgeBlob(mediaId);
      saveBlob(blob, `vastavik-${kind}-${ownerKey}.png`);
    } catch (err) {
      console.error(`Failed to download ${kind}:`, err);
    } finally {
      setAssetLoading(null);
    }
  };
if (!detailsFile) return null;
  const f = detailsFile;
  const hasVastavikId = Boolean(f.ownerKey ?? f.meta?.ownerKey);
  const protType = String(f.protectionType ?? f.meta?.protectionType ?? "").toLowerCase();
  const isQrProtected =
    protType === "qr" || protType === "qr_watermark" || protType === "qrcode"
      ? true
      : protType === "vastavik_id" || protType === "ownerkey" || protType === "owner_key"
        ? false
        : Boolean(f.qrEmbedded ?? f.meta?.qrEmbedded);

  const fileName = f.displayName || f.name || f.originalFilename || "media";

  // Reusable action buttons logic block to maintain original state dependencies
  const renderActionButtons = () => {
    const phase = getUploadPhase(f);
    const backendCanVerify = f.canVerifyWithOriginal ?? f.meta?.canVerifyWithOriginal;
    const rejectionCode = String(f.rejectionCode ?? f.meta?.rejectionCode ?? "").toUpperCase();
       const rejectionReason = f.rejectionReason ?? f.meta?.rejectionReason;
    const TERMINAL_REJECTION_CODES = ["SCREENSHOT", "SCREEN RECORDING", "DEEPFAKE", "DEEPFAKE_CHECK_UNAVAILABLE", "DEEPFAKE_UNSUPPORTED_FORMAT", "DEEPFAKE_CHECK_FAILED", "DUPLICATE", "ALREADY_REGISTERED", "ALREADY REGISTERED"];
  const terminalRejection = backendCanVerify != null
                  ? !backendCanVerify
                  : rejectionCode
                    ? TERMINAL_REJECTION_CODES.includes(rejectionCode)
                    : /screenshot|screen recording|deepfake|duplicate|uploaded before|already been|already registered/i.test(String(rejectionReason || ""));
    const mediaKind = String(f.type || "").startsWith("video/") ? "video" : String(f.type || "").startsWith("audio/") ? "audio" : "image";
    const mediaName = f.displayName || f.name || f.originalFilename || "media";
    const canRecover = phase.phase === "failed" && !f.matchVerification && !terminalRejection && /^(image|video|audio)\//.test(String(f.type || ""));

    return (
      <>
        {canRecover && (
          <>
            <button
              className="w-full py-3 rounded-md border border-gray-300 dark:border-white/20 bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
              onClick={() => { setVerifyNoOriginal(false); setVerifyOpen(true); }}
            >
              {mediaKind === "video" ? "Validate with original — upload the unedited video" : mediaKind === "audio" ? "Validate with original — upload the unedited audio" : "Validate with original — upload the unedited photo"}
            </button>
            <button
              className="w-full py-3 rounded-md border border-gray-300 dark:border-white/20 bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
              onClick={() => { setSupportPreset({ slug: "missing_original", subject: `No original available: ${mediaName}`, message: `I don't have the original for my upload "${mediaName}".\n\nHere's some context: ` }); setSupportOpen(true); }}
            >
              I don't have the original
            </button>
          </>
        )}
        {phase.phase === "failed" && (
          <button
            className="w-full py-3 rounded-md border border-gray-300 dark:border-white/20 bg-transparent text-black dark:text-white text-[12px] sm:text-[13px] font-['Product_Sans'] hover:bg-gray-50 dark:hover:bg-white/5 transition"
            onClick={() => { setSupportPreset({ slug: "appeal", subject: `Appeal: ${mediaName}`, message: `I'd like to appeal the decision on my upload "${mediaName}".\n\nI believe it should be validated because: ` }); setSupportOpen(true); }}
          >
            Think this is an error? Report and our team will review.
          </button>
        )}
      </>
    );
  };

return (
  <>
    <div
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/40 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto [&::-webkit-scrollbar]:hidden"
      style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-5xl flex flex-col rounded-xl bg-white dark:bg-[#0a0a0a] text-black dark:text-white border border-black dark:border-white shadow-2xl overflow-hidden my-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 md:px-6 py-4 shrink-0 bg-white dark:bg-[#0a0a0a] border-b border-black dark:border-white">
          <div className="min-w-0 pr-3">
            <div className="text-sm md:text-base font-semibold truncate tracking-wide font-['Product_Sans']">
              {fileName}
            </div>
            <div className="text-xs text-gray-500 dark:text-gray-400 truncate font-['Product_Sans']">{f.name || f.originalFilename}</div>
          </div>
          <button
            className="text-gray-500 hover:text-black dark:hover:text-white transition shrink-0"
            onClick={onClose}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
          </button>
        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-y-auto md:overflow-hidden bg-white dark:bg-[#0a0a0a] [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
          {/* Left Column (Image & Actions Desktop) */}
          <div className="w-full md:w-[45%] p-5 md:p-6 flex flex-col border-b md:border-b-0 md:border-r border-black dark:border-white shrink-0 md:overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>
            <div className="w-full flex items-center justify-center bg-transparent mb-4 relative">
              {f.type?.startsWith("image/") && f.url ? (
                <img
                  src={f.url}
                  alt={fileName}
                  className="w-full h-auto max-h-[40vh] md:max-h-[50vh] object-contain shadow-sm border border-black dark:border-white rounded"
                  onError={(e) => {
                    (e.currentTarget as HTMLImageElement).src =
                      "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23222'/%3E%3Ctext x='50' y='50' font-family='Arial' font-size='14' fill='%23fff' text-anchor='middle' dy='.3em'%3EImage%3C/text%3E%3C/svg%3E";
                  }}
                />
              ) : f.type?.startsWith("video/") && f.url ? (
                <div className="w-full flex flex-col items-center justify-center">
                  <video
                    src={f.url.includes("#") ? f.url : `${f.url}#t=0.001`}
                    controls
                    preload="metadata"
                    className="w-full h-auto max-h-[40vh] md:max-h-[50vh] rounded shadow-sm border border-black dark:border-white bg-black/5 dark:bg-black/20"
                    poster={f.thumbnailUrl || undefined}
                  />
                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center mt-2 font-['Product_Sans']">
                    Video player controls available
                  </div>
                </div>
              ) : (
                <div className="w-full h-48 flex items-center justify-center border border-black dark:border-white rounded bg-gray-50 dark:bg-white/5">
                  <span className="text-4xl text-gray-400">📄</span>
                </div>
              )}
            </div>

            <button
              className="w-full py-3 bg-black text-white dark:bg-white dark:text-black rounded uppercase text-[11px] font-bold tracking-[0.15em] hover:opacity-90 transition mb-6 shrink-0"
              onClick={open}
              disabled={!f.url}
            >
              Open Full Preview
            </button>

            {/* Desktop Actions */}
            <div className="hidden md:flex flex-col space-y-3">
              {renderActionButtons()}
            </div>
          </div>

          {/* Right Column (Details) */}
          <div className="w-full md:w-[55%] p-5 md:p-6 flex flex-col space-y-6 md:overflow-y-auto [&::-webkit-scrollbar]:hidden"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}>

            {/* Status Grid */}
            <div className="grid grid-cols-3 gap-2">
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Status</span>
                {f.status ? (
                  <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 border text-[10px] uppercase tracking-wider font-bold rounded w-max ${f.status === "CONFIRMED" || f.status === "COMPLETED" ? "border-emerald-500/40 text-emerald-500" : f.status === "FAILED" ? "border-red-500/40 text-red-500" : "border-yellow-500/40 text-yellow-500"}`}>
                    {f.status === "FAILED" && <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>}
                    {f.status}
                  </span>
                ) : (
                  <span className="text-[11px] text-gray-400">—</span>
                )}
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Uploaded</span>
                <span className="text-[11px] text-black dark:text-white font-medium">{formatDateUK(f.createdAt)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-gray-400 font-bold mb-1.5 font-['Product_Sans']">Size</span>
                <span className="text-[11px] text-black dark:text-white font-medium">{formatBytes(f.size || Number(f.fileSize || 0))}</span>
              </div>
            </div>

            <hr className="border-black dark:border-white" />

            {/* Technical Identifiers */}
            {(() => {
              const ownerKey = f.ownerKey ?? f.meta?.ownerKey;
              const nftToken = f.nftToken ?? f.nftTokenId ?? f.meta?.nftTokenId;
              const mediaId = f.mediaId ?? f.meta?.mediaId ?? f.id;

              return (
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold mb-4 font-['Product_Sans']">Technical Identifiers</div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">NFT Token</span>
                      <span className="text-black dark:text-gray-300 font-medium truncate max-w-[150px] sm:max-w-[200px] text-right">{nftToken || "—"}</span>
                    </div>
                    <hr className="border-black/20 dark:border-white/20" />

                    <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Vastavik ID</span>
                      <span className="text-black dark:text-gray-300 font-medium truncate max-w-[150px] sm:max-w-[200px] text-right">{ownerKey || "—"}</span>
                    </div>
                    <hr className="border-black/20 dark:border-white/20" />

                    <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Media ID</span>
                      <span className="bg-gray-100 dark:bg-[#222] text-black dark:text-gray-300 px-2 py-1 rounded truncate max-w-[150px] sm:max-w-[250px] text-right">{mediaId}</span>
                    </div>
                    <hr className="border-black/20 dark:border-white/20" />

                    <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                      <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Type</span>
                      <span className="text-black dark:text-gray-300 font-medium text-right">{f.type || f.mimeType || "—"}</span>
                    </div>

                    {f.type?.startsWith("image/") && f.dimensions?.width && f.dimensions?.height && (
                      <>
                        <hr className="border-black/20 dark:border-white/20" />
                        <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                          <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Dimensions</span>
                          <span className="text-black dark:text-gray-300 font-medium text-right">{f.dimensions.width} × {f.dimensions.height} px</span>
                        </div>
                      </>
                    )}

                    {(f.isAudio || f.type?.startsWith("audio/")) && f.audioMetadata && (
                       <>
                        {f.audioMetadata.duration && (
                          <>
                            <hr className="border-black/20 dark:border-white/20" />
                            <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                              <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Duration</span>
                              <span className="text-black dark:text-gray-300 font-medium text-right">{formatDuration(f.audioMetadata.duration)}</span>
                            </div>
                          </>
                        )}
                        {f.audioMetadata.bitrate && (
                           <>
                             <hr className="border-black/20 dark:border-white/20" />
                             <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                               <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Bitrate</span>
                               <span className="text-black dark:text-gray-300 font-medium text-right">{Math.round(f.audioMetadata.bitrate / 1000)} kbps</span>
                             </div>
                           </>
                        )}
                       </>
                    )}
                  </div>
                </div>
              );
            })()}

            <hr className="border-black dark:border-white" />

            {/* Verification & Protection */}
            {(() => {
                const ownerKey = f.ownerKey ?? f.meta?.ownerKey;
                const verifyUrl = f.verificationUrl ?? f.meta?.verificationUrl ?? (ownerKey ? `https://vastavik.org/verify/${ownerKey}` : null);
                const protectionType = f.protectionType ?? f.meta?.protectionType;
                const protectionLabel = protectionType
                  ? ({ qr: "QR Code", qr_watermark: "QR Code", vastavik_id: "Vastavik ID", ownerkey: "Vastavik ID", voiceover: "Voiceover (Voice ID)" } as any)[String(protectionType).toLowerCase()]
                    ?? (String(protectionType).charAt(0).toUpperCase() + String(protectionType).slice(1))
                  : null;
                const txId = f.blockchainTxId ?? f.txId ?? f.meta?.blockchainTxId;
                const phase = getUploadPhase(f);
                const blockchainLabel = phase.phase === "failed" ? "REJECTED" : phase.phase === "verifying" ? "VERIFYING" : phase.phase === "processing" ? "PENDING" : "REGISTERED";
                const blockchainColor = phase.phase === "failed" ? "border-red-500/40 text-red-500" : phase.phase === "verifying" ? "border-emerald-500/40 text-emerald-500" : phase.phase === "processing" ? "border-yellow-500/40 text-yellow-500" : "border-emerald-500/40 text-emerald-500";

                const rejectionReason = f.rejectionReason ?? f.meta?.rejectionReason;
                const backendCanVerify = f.canVerifyWithOriginal ?? f.meta?.canVerifyWithOriginal;
                const rejectionCode = String(f.rejectionCode ?? f.meta?.rejectionCode ?? "").toUpperCase();
                const TERMINAL_REJECTION_CODES = ["SCREENSHOT", "SCREEN RECORDING", "DEEPFAKE", "DEEPFAKE_CHECK_UNAVAILABLE", "DEEPFAKE_UNSUPPORTED_FORMAT", "DEEPFAKE_CHECK_FAILED", "DUPLICATE", "ALREADY_REGISTERED", "ALREADY REGISTERED"];
                const terminalRejection = backendCanVerify != null ? !backendCanVerify : rejectionCode ? TERMINAL_REJECTION_CODES.includes(rejectionCode) : /screenshot|screen recording|deepfake|duplicate|uploaded before|already been|already registered/i.test(String(rejectionReason || ""));

                return (
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold mb-4 font-['Product_Sans']">Verification & Protection</div>
                    <div className="space-y-4">

                      {phase.phase === "failed" && !terminalRejection && !f.matchVerification &&
                        /^(image|video|audio)\//.test(f.type || "") && (
                        <div className="flex items-center gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-200/90 font-['Product_Sans']">
                          <span className="text-sm leading-none animate-bounce">↓</span>
                          <span>You can still validate this — scroll down to upload your original.</span>
                        </div>
                      )}

                      {phase.phase === "verifying" && (
                        <div className="flex items-start gap-2 rounded-lg border border-emerald-500/25 bg-emerald-500/10 px-3 py-2 text-[11px] text-emerald-700 dark:text-emerald-200/90 font-['Product_Sans']">
                          <span className="mt-0.5 inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-emerald-500/40 border-t-emerald-500" />
                          <span>Verifying your original against this media… Your library will update automatically once it’s done.</span>
                        </div>
                      )}

                      <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                        <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Blockchain</span>
                        <span className={`px-2 py-0.5 border text-[9px] uppercase tracking-widest font-bold rounded ${blockchainColor}`}>{blockchainLabel}</span>
                      </div>

                      {protectionLabel && (
                        <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                          <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Protection</span>
                          <span className="text-black dark:text-gray-300 font-medium text-right">{protectionLabel}</span>
                        </div>
                      )}

                      {verifyUrl && (
                        <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                          <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Verify URL</span>
                          <a href={verifyUrl} target="_blank" rel="noreferrer" className="text-blue-500 dark:text-blue-400 hover:underline max-w-[150px] sm:max-w-[200px] truncate text-right">{verifyUrl}</a>
                        </div>
                      )}

                      {txId && (
                        <div className="flex items-center justify-between text-[11px] font-['Product_Sans'] gap-2">
                          <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider shrink-0">Transaction ID</span>
                          <span className="text-black dark:text-gray-300 max-w-[150px] sm:max-w-[200px] truncate text-right">{txId}</span>
                        </div>
                      )}
                    </div>

                    {phase.phase === "failed" && !terminalRejection && (
                      <div className="mt-5 border border-red-500/40 rounded-lg p-4 relative bg-red-50/50 dark:bg-transparent">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg"></div>
                        <div className="flex items-center gap-2 mb-3 text-red-500 text-[10px] uppercase font-bold tracking-widest font-['Product_Sans']">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                          {VALIDATION_FAILED_TITLE}
                        </div>
                        <ol className="list-decimal pl-4 space-y-1.5 text-[11px] text-gray-700 dark:text-gray-300 font-['Product_Sans'] leading-relaxed">
                          {VALIDATION_FAILED_REASONS.map((r) => (<li key={r}>{r}</li>))}
                        </ol>
                        <div className="text-[11px] text-gray-700 dark:text-gray-300 mt-3 font-['Product_Sans'] leading-relaxed">{VALIDATION_FAILED_HELP}</div>
                      </div>
                    )}

                    {phase.phase === "failed" && terminalRejection && rejectionReason && (
                      <div className="mt-5 border border-red-500/40 rounded-lg p-4 relative bg-red-50/50 dark:bg-transparent">
                        <div className="absolute left-0 top-0 bottom-0 w-1 bg-red-500 rounded-l-lg"></div>
                        <div className="flex items-center gap-2 mb-2 text-red-500 text-[10px] uppercase font-bold tracking-widest font-['Product_Sans']">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><line x1="12" y1="8" x2="12" y2="12"></line><line x1="12" y1="16" x2="12.01" y2="16"></line></svg>
                          Reason
                        </div>
                        <div className="text-[11px] text-gray-700 dark:text-gray-300 font-['Product_Sans'] leading-relaxed">{rejectionReason}</div>
                      </div>
                    )}
                  </div>
                );
            })()}

            {/* Voiceover (audio) */}
            {(f.isAudio || f.type?.startsWith("audio/")) && (
              <>
                <hr className="border-black dark:border-white" />
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div className="text-[11px] uppercase tracking-widest text-black dark:text-white font-bold font-['Product_Sans']">Voiceover</div>
                    <div className="flex gap-2">
                      {voiceoverStatusLoading || isRefreshingVoiceover ? (
                        <button disabled className="rounded border border-black dark:border-white bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-gray-500 flex items-center"><Spinner className="h-3 w-3 mr-1 text-gray-400" /> Loading...</button>
                      ) : f.voiceoverGenerated && f.voiceoverUrl ? (
                        <button onClick={handleShareVoiceover} className="rounded border border-black dark:border-white bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition">Share</button>
                      ) : (
                        <button onClick={handleCheckVoiceoverStatus} className="rounded border border-black dark:border-white bg-transparent px-2 py-1 text-[10px] uppercase tracking-wider text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition">Refresh</button>
                      )}
                    </div>
                  </div>
                  {!voiceoverStatusLoading && f.voiceoverPending && (<div className="flex justify-between text-[11px] font-['Product_Sans']"><span className="text-gray-500 uppercase tracking-wider">Status</span><span className="text-yellow-600 dark:text-yellow-400 font-medium">⏳ Being generated</span></div>)}
                  {!voiceoverStatusLoading && f.voiceoverGenerated && f.voiceoverUrl && (
                    <div className="space-y-2 mt-2">
                      <div className="text-[11px] text-gray-500 dark:text-gray-400 uppercase tracking-wider font-['Product_Sans']">Vastavik Voice ID</div>
                      <audio controls className="w-full h-9"><source src={f.voiceoverUrl} type="audio/mpeg" /></audio>
                    </div>
                  )}
                  {!voiceoverStatusLoading && !f.voiceoverGenerated && !f.voiceoverPending && (<div className="flex justify-between text-[11px] font-['Product_Sans']"><span className="text-gray-500 uppercase tracking-wider">Status</span><span className="text-gray-400">No voiceover generated yet</span></div>)}
                </div>
              </>
            )}

            {/* Mobile Actions (Hidden on Desktop) */}
            <div className="flex md:hidden flex-col space-y-3 mt-6 pt-6 border-t border-black dark:border-white">
              {renderActionButtons()}
            </div>

          </div>
        </div>

        {/* Footer — download / QR / badge actions (restored from original) */}
        <div className="flex flex-wrap justify-end gap-2 sm:gap-3 border-t border-black dark:border-white px-5 md:px-6 py-4 shrink-0 bg-white dark:bg-[#0a0a0a]">
          {hasVastavikId && (
            isQrProtected ? (
              <button
                className="py-2 px-4 rounded-md border border-black dark:border-white text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
                onClick={() => downloadAsset("qr")}
                disabled={assetLoading !== null}
              >
                {assetLoading === "qr" ? <Spinner /> : null}Download Vastavik QR
              </button>
            ) : (
              <button
                className="py-2 px-4 rounded-md border border-black dark:border-white text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
                onClick={() => downloadAsset("badge")}
                disabled={assetLoading !== null}
              >
                {assetLoading === "badge" ? <Spinner /> : null}Download Vastavik ID Badge
              </button>
            )
          )}
          {getUploadPhase(f).phase !== "failed" && (
            <button
              className="py-2 px-5 rounded-md border border-black dark:border-white text-black dark:text-white hover:bg-gray-50 dark:hover:bg-white/10 transition disabled:opacity-40 text-[12px] sm:text-[13px] font-['Product_Sans']"
              onClick={download}
              disabled={!f.url || downloading}
            >
              {downloading ? "Downloading…" : "Download"}
            </button>
          )}
          <button
            className="py-2 px-5 rounded-md bg-black text-white dark:bg-white dark:text-black hover:opacity-90 transition disabled:opacity-40 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.15em]"
            onClick={open}
            disabled={!f.url}
          >
            Open
          </button>
        </div>
      </div>
    </div>

    {verifyOpen && (
      <VerifyOriginalModal
        mediaId={String(f.mediaId ?? f.meta?.mediaId ?? f.id)}
        mediaName={f.displayName || f.name}
        mediaKind={f.type?.startsWith("video/") ? "video" : f.type?.startsWith("audio/") ? "audio" : "image"}
        startNoOriginal={verifyNoOriginal}
        onClose={() => setVerifyOpen(false)}
        onGoToLibrary={() => {
          setVerifyOpen(false);
          onClose();
          navigate("/library");
        }}
        onVerified={() => {
          setDetailsFile((prev: any) =>
            prev ? { ...prev, matchVerification: { verdict: "DERIVED_ENHANCEMENT" } } : prev
          );
          try { window.dispatchEvent(new Event("vastavik:upload-completed")); } catch { /* noop */ }
        }}
      />
    )}
    {supportOpen && (
      <SupportTicketModal
        open={supportOpen}
        onClose={() => setSupportOpen(false)}
        presetCategorySlug={supportPreset?.slug}
        presetSubject={supportPreset?.subject}
        presetMessage={supportPreset?.message}
        relatedMediaId={String(f.mediaId ?? f.meta?.mediaId ?? f.id)}
      />
    )}
  </>
);
}